import React, { useEffect, useState } from "react";
import { loadCatalog, verifiedBytes } from "../../api/tari-registry";

const repository = "https://github.com/kstrycharz/tari-agent-registry";
const labels = { skill: "Skill", agent: "Agent", mcp: "MCP server" };
const folders = { skill: "skills", agent: "agents", mcp: "mcps" };
const filters = [["all", "All resources"], ["skill", "Skills"], ["agent", "Agents"], ["mcp", "MCP servers"]];
const external = { target: "_blank", rel: "noopener noreferrer" };

function PackageCard({ entry }) {
  const [download, setDownload] = useState({ state: "idle", message: "" });
  async function save() {
    setDownload({ state: "busy", message: "Checking download integrity…" });
    try {
      const bytes = await verifiedBytes(entry);
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = entry.download.split("/").pop();
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownload({ state: "done", message: "SHA-256 verified. Inspect the files before installation." });
    } catch (error) {
      setDownload({ state: "error", message: `Download stopped: ${error.message}` });
    }
  }
  return (
    <article className="registry-card">
      <div className="registry-card-meta">
        <span className="tag">{labels[entry.kind]}</span>
        <span>v{entry.version}</span>
      </div>
      <h3>{entry.name}</h3>
      <p className="registry-description">{entry.description}</p>
      <div className="registry-permissions">
        <span className={`registry-risk registry-risk-${entry.risk}`}>{entry.risk} declared risk</span>
        <span>{entry.permissions.length ? entry.permissions.join(" · ") : "No permissions requested"}</span>
      </div>
      <details className="registry-details">
        <summary>Compatibility &amp; package details</summary>
        <dl>
          <dt>Compatibility</dt><dd>{entry.compatibility}</dd>
          <dt>License</dt><dd>{entry.license}</dd>
          <dt>Security notes</dt><dd>{entry.security_notes}</dd>
          <dt>SHA-256</dt><dd className="registry-checksum">{entry.sha256}</dd>
        </dl>
      </details>
      <div className="registry-card-actions">
        <button className="button primary" onClick={save} disabled={download.state === "busy"} aria-label={`Download ${entry.name}`}>
          {download.state === "busy" ? "Verifying…" : "Download ZIP"}
        </button>
        <a className="text-link" href={`${repository}/tree/main/packages/${folders[entry.kind]}/${encodeURIComponent(entry.id)}`} {...external}>View source ↗</a>
      </div>
      <p className={`registry-download-status ${download.state === "error" ? "registry-error" : ""}`} role="status">{download.message}</p>
    </article>
  );
}

export default function AgentRegistry() {
  const [entries, setEntries] = useState([]);
  const [state, setState] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  useEffect(() => {
    let active = true;
    setState("loading");
    loadCatalog().then((feed) => {
      if (active) {
        setEntries(feed.packages.filter((entry) => entry.status === "active"));
        setState("ready");
      }
    }).catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [attempt]);
  const visible = entries.filter((entry) => (filter === "all" || entry.kind === filter) &&
    `${entry.name} ${entry.description}`.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <section id="ai-tools" className="registry-section" aria-labelledby="registry-title">
      <div className="section-heading">
        <div><div className="eyebrow">BUILD WITH THE TARI COMMUNITY</div><h2 id="registry-title">Tari AI Resources</h2></div>
        <div className="registry-heading-actions">
          <a className="button primary" href={`${repository}/blob/main/CONTRIBUTING.md`} {...external}>Submit a resource ↗</a>
          <a className="button secondary" href={repository} {...external}>GitHub repository ↗</a>
        </div>
      </div>
      <p className="registry-intro">Skills, agents, and MCP servers for your next Tari build. Download the source, inspect it, and connect it to your preferred assistant.</p>
      <p className="registry-submission-note">Share your own resource through a GitHub pull request. Submissions go through automated checks and maintainer review.</p>
      <div className="registry-controls">
        <div className="tabs registry-tabs" role="group" aria-label="Resource type">
          {filters.map(([value, label]) => <button type="button" key={value} className={filter === value ? "selected" : ""} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>)}
        </div>
        <label className="registry-search"><span>Search resources</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or purpose" /></label>
      </div>
      <p className="registry-guidance">Community maintained. Automated checks do not certify safety. Permissions are declared by contributors; review every file before installation.</p>
      {state === "loading" && <p className="registry-state" role="status">Loading community resources…</p>}
      {state === "error" && <div className="registry-state" role="status">The resource catalog is unavailable. <button className="button secondary" onClick={() => setAttempt(attempt + 1)}>Try loading resources again</button><a className="text-link" href={repository} {...external}>Browse the repository ↗</a></div>}
      {state === "ready" && <>
        <p className="registry-result-count" role="status">{visible.length} {visible.length === 1 ? "resource" : "resources"}{filter === "all" ? " available" : ` in ${filters.find(([value]) => value === filter)[1]}`}</p>
        {visible.length ? <div className="registry-grid">{visible.map((entry) => <PackageCard key={`${entry.kind}/${entry.id}/${entry.version}`} entry={entry} />)}</div> : <p className="registry-state">No resources match this selection. Try another type or search.</p>}
      </>}
    </section>
  );
}
